import {
  Box,
  Button,
  ScrollArea,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core'
import { IconMessagePlus } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import {
  listGenieConversations,
  type GenieConversationSummary,
} from '../../api/genie'
import { QueryState, Empty } from '../intelligence/shared'

function formatConversationTimestamp(ms?: number): string | null {
  if (!ms) return null
  const date = new Date(ms)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function conversationLabel(conversation: GenieConversationSummary): string {
  return conversation.title?.trim() || 'Untitled conversation'
}

interface GenieConversationHistoryProps {
  spaceId: string
  activeConversationId: string | null
  onSelectConversation: (conversation: GenieConversationSummary) => void
  onNewChat: () => void
}

export function GenieConversationHistory({
  spaceId,
  activeConversationId,
  onSelectConversation,
  onNewChat,
}: GenieConversationHistoryProps) {
  const {
    data: conversations = [],
    isLoading,
    isError,
    error,
  } = useQuery(
    ['genie', 'conversations', spaceId],
    () => listGenieConversations(spaceId),
    { retry: 1 }
  )

  return (
    <Box
      w={260}
      style={{
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid var(--mantine-color-gray-3)',
      }}
    >
      <Box
        p="sm"
        style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}
      >
        <Button
          fullWidth
          variant="light"
          color="prism"
          leftSection={<IconMessagePlus size={16} />}
          onClick={onNewChat}
        >
          New chat
        </Button>
      </Box>

      <ScrollArea flex={1} p="xs">
        <QueryState error={isError ? error : null} loading={isLoading} />

        {!isLoading && !isError && conversations.length === 0 && (
          <Box p="sm">
            <Empty message="No past chats yet." />
          </Box>
        )}

        {!isLoading && !isError && conversations.length > 0 && (
          <Stack gap={2}>
            {conversations.map((conversation) => {
              const isActive = conversation.id === activeConversationId
              const timestamp = formatConversationTimestamp(
                conversation.lastUpdatedAt ?? conversation.createdAt
              )
              return (
                <UnstyledButton
                  key={conversation.id}
                  onClick={() => onSelectConversation(conversation)}
                  p="sm"
                  style={{
                    borderRadius: 6,
                    backgroundColor: isActive
                      ? 'var(--mantine-color-prism-0)'
                      : 'transparent',
                  }}
                >
                  <Text
                    size="sm"
                    fw={isActive ? 600 : 500}
                    c={isActive ? 'prism.7' : 'gray.8'}
                    lineClamp={2}
                  >
                    {conversationLabel(conversation)}
                  </Text>
                  {timestamp && (
                    <Text size="xs" c="dimmed" mt={2}>
                      {timestamp}
                    </Text>
                  )}
                </UnstyledButton>
              )
            })}
          </Stack>
        )}
      </ScrollArea>
    </Box>
  )
}
